import type { ChatMessage, ConversationMeta } from "./types";
import { truncate } from "./helpers";

const MESSAGES_KEY = "agentme-chat-messages";
const CONVERSATIONS_KEY = "agentme-conversations";
const FEEDBACK_KEY = "agentme-chat-feedback";
const LAST_ACTIVE_SESSION_KEY = "agentme-last-active-chat-session";
const MAX_PERSISTED = 200;

// ---- Message persistence ----

export function loadMessages(sessionId: string): ChatMessage[] {
  try {
    const raw = localStorage.getItem(`${MESSAGES_KEY}-${sessionId}`);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return [];
}

export function saveMessages(sessionId: string, messages: ChatMessage[]) {
  try {
    localStorage.setItem(
      `${MESSAGES_KEY}-${sessionId}`,
      JSON.stringify(messages.slice(-MAX_PERSISTED)),
    );
    // Update conversation metadata
    updateConversationMeta(sessionId, messages);
  } catch {
    // ignore
  }
}

// ---- Conversation management ----

function getConversationsMeta(): ConversationMeta[] {
  try {
    const raw = localStorage.getItem(CONVERSATIONS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return [];
}

function saveConversationsMeta(conversations: ConversationMeta[]) {
  try {
    localStorage.setItem(CONVERSATIONS_KEY, JSON.stringify(conversations));
  } catch {
    // ignore
  }
}

function updateConversationMeta(sessionId: string, messages: ChatMessage[]) {
  if (messages.length === 0) return;

  const conversations = getConversationsMeta();
  const existing = conversations.findIndex((c) => c.id === sessionId);
  const firstUserMsg = messages.find((m) => m.role === "user");
  const lastMsg = messages[messages.length - 1];

  const meta: ConversationMeta = {
    id: sessionId,
    title: firstUserMsg ? truncate(firstUserMsg.content, 60) : "New conversation",
    lastMessage: lastMsg ? truncate(lastMsg.content, 80) : "",
    timestamp: lastMsg?.timestamp ?? Date.now(),
    messageCount: messages.length,
  };

  if (existing >= 0) {
    conversations[existing] = meta;
  } else {
    conversations.push(meta);
  }

  // Sort by most recent
  conversations.sort((a, b) => b.timestamp - a.timestamp);
  saveConversationsMeta(conversations);
}

export function loadConversationList(): ConversationMeta[] {
  return getConversationsMeta();
}

export function loadLastActiveConversationId(): string | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_SESSION_KEY);
    return typeof raw === "string" && raw.trim().length > 0 ? raw : null;
  } catch {
    return null;
  }
}

export function saveLastActiveConversationId(sessionId: string) {
  try {
    localStorage.setItem(LAST_ACTIVE_SESSION_KEY, sessionId);
  } catch {
    // ignore
  }
}

export function deleteConversation(sessionId: string) {
  try {
    localStorage.removeItem(`${MESSAGES_KEY}-${sessionId}`);
    const conversations = getConversationsMeta().filter((c) => c.id !== sessionId);
    saveConversationsMeta(conversations);
    if (loadLastActiveConversationId() === sessionId) {
      const next = conversations[0]?.id ?? "";
      if (next) {
        saveLastActiveConversationId(next);
      } else {
        localStorage.removeItem(LAST_ACTIVE_SESSION_KEY);
      }
    }
  } catch {
    // ignore
  }
}

export function renameConversation(sessionId: string, title: string) {
  const conversations = getConversationsMeta();
  const idx = conversations.findIndex((c) => c.id === sessionId);
  if (idx >= 0) {
    conversations[idx].title = title;
    saveConversationsMeta(conversations);
  }
}

// ---- Feedback persistence ----

interface FeedbackEntry {
  messageId: string;
  feedback: "positive" | "negative";
}

export function loadFeedback(): FeedbackEntry[] {
  try {
    const raw = localStorage.getItem(FEEDBACK_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return [];
}

export function saveFeedback(messageId: string, feedback: "positive" | "negative") {
  const entries = loadFeedback();
  const existing = entries.findIndex((e) => e.messageId === messageId);
  if (existing >= 0) {
    if (entries[existing].feedback === feedback) {
      // Toggle off
      entries.splice(existing, 1);
    } else {
      entries[existing].feedback = feedback;
    }
  } else {
    entries.push({ messageId, feedback });
  }
  try {
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify(entries));
  } catch {
    // ignore
  }
}

export function getFeedback(messageId: string): "positive" | "negative" | null {
  const entries = loadFeedback();
  const entry = entries.find((e) => e.messageId === messageId);
  return entry?.feedback ?? null;
}
